/**
 * 툴팁 — 아이템 위에 올리면 설명이 뜬다.
 *
 * "축복받은 강화석이 뭐였지?" 라는 물음이 나왔다는 건, 쓰는 자리에만 설명이 있고
 * **물건 자체에는 없었다**는 뜻이다. 가진 것이 무엇인지는 가진 자리에서 알아야 한다.
 *
 * `title` 속성은 뜨는 데 1초 넘게 걸리고 모바일에서는 아예 안 뜬다. 직접 그린다.
 */
import { S, esc } from '../core/state.js';
import { pixelGlyphText } from '../render/pixel-glyphs.js';
import { SLOT_NAME } from '../world/gearnames.js';
import { LIFE_STONE_SECS, LIFE_STONE_LEVELS } from '../world/cycle.js';
// ⚠ 승급 규칙 문장은 **여기서 다시 적지 않는다** — `EX` 때 네 자리가 한꺼번에 낡았다
import { ascendRule, eqTitle, guildRingInfo, isTempleRingItem, templeRingInfo, junkName, junkPriceMultOf, rollDesc } from './panels/common.js';
import {
  ACC_BY_ID, GRADE_COL, JUNK_KO, JUNK_PRICE, MAX_GRADE, MAX_PLUS, POTIONS, SHARD_KEY, ULTIMATE_BSTONES, ULTIMATE_CHANCE, floorLabel, gradeOf,
  CARRY_BY_KEY, LIFE_STONE_KEY, GUARD_SHARD_KEY, GUARD_SHARD_NAME,
  PARDON_BY_KEY, PASSIVE_BY_KEY, SKILL_IMPRINT_BY_KEY, WING_BY_KEY, passiveBookSourceText,
} from '../world/map.js';

const BSTONE = 'bstone';

/** 재료·소모품 설명. 화면 여러 곳이 같은 문장을 쓰도록 여기 모은다 */
export const TIPS = {
  [SHARD_KEY]: {
    name: '강화석',
    desc: '대장간에서 <b>하급 장비를 제작</b>하거나, '
      + '안전수치 위에서 <b>확률 강화</b>에 쓴다. 실패해도 장비는 부서지지 않는다.',
    from: '모든 몬스터가 낮은 확률로 떨군다.',
  },
  [BSTONE]: {
    name: '축복받은 강화석',
    desc: `성공하면 <b>1~3단계</b>가 한 번에 오른다. 일반 구간의 성공률은 강화석과 같다. `
      + `궁극 장비 +${MAX_PLUS} 이상은 <b>축복받은 강화석 ${ULTIMATE_BSTONES.toLocaleString()}개 · 성공률 ${ULTIMATE_CHANCE * 100}% 고정</b>. `
      + '실패하면 재료만 소모되고 장비는 유지된다.',
    from: '<b>구역 보스</b>가 확정으로, 네임드가 아주 가끔 떨군다.',
  },
};

/**
 * ★ **잡템 설명을 손으로 세 줄 적어놨더니 t4~t7 이 통째로 설명 없이 살았다.**
 * 띠를 넣을 때 여기를 같이 고치는 것을 사람의 기억에 맡긴 결과다 —
 * `items.ts` 가 같은 병으로 이미 한 번 데였고(77종이 같은 값), 그쪽은 표로 고쳤다.
 * 여기도 **표(`JUNK_KO`)에서 돌린다.** 띠가 늘면 설명이 따라 생긴다.
 *
 * ⚠ 나오는 자리(`from`)만 사람이 적는다 — 그건 표에 없는 정보라서다.
 *   안 적힌 띠는 그 줄을 빼고 뜬다. **없는 채로 뜨는 것이 안 뜨는 것보다 낫다.**
 */
const JUNK_FROM = {
  junk_t1: '들판 몬스터 드롭', junk_t2: '숲 몬스터 드롭', junk_t3: '절벽·갱도 몬스터 드롭',
  junk_t4: '섬 끝자락·깊은 갱도 몬스터 드롭', junk_t5: '본토 벌판 몬스터 드롭',
  junk_t6: '잿빛 황야 몬스터 드롭', junk_t7: '검은 늪·제단 몬스터 드롭',
  junk_t8: `${floorLabel(4)} 몬스터 드롭`,
  junk_t9: `${floorLabel(5)} 몬스터 드롭`,
  junk_t10: `${floorLabel(6)} 몬스터 드롭`,
  junk_t11: `${floorLabel(7)} 몬스터 드롭`,
  junk_t12: `${floorLabel(8)} 몬스터 드롭`,
  junk_t13: `${floorLabel(9)} 몬스터 드롭`,
  junk_t14: `${floorLabel(10)} 몬스터 드롭`,
  junk_t15: '비늘바람 협곡 몬스터 드롭',
  junk_t16: '유리사막 몬스터 드롭',
  junk_t17: `${floorLabel(11)} 몬스터 드롭`,
  junk_t18: `${floorLabel(12)} 몬스터 드롭`,
  junk_t19: `${floorLabel(13)} 몬스터 드롭`,
  junk_t20: `${floorLabel(14)} 몬스터 드롭`,
  junk_t21: `${floorLabel(15)} 몬스터 드롭`,
  junk_t22: `${floorLabel(16)} 몬스터 드롭`,
  junk_t23: `${floorLabel(17)} 몬스터 드롭`,
  junk_t24: `${floorLabel(18)} 몬스터 드롭`,
  junk_t25: 'Lv99~103 몬스터 드롭',
  junk_t26: '검은가시 분지 Lv104~108 몬스터 드롭',
  junk_t27: '검은가시 분지 Lv109~113 몬스터 드롭',
  junk_t28: '검은가시 분지 Lv114~118 몬스터 드롭',
  junk_t29: '검은가시 분지 Lv119~123 몬스터 드롭',
  junk_t30: '검은가시 분지 Lv124~128 몬스터 드롭',
  junk_t31: '검은가시 분지 Lv129~133 몬스터 드롭',
  junk_t32: '검은가시 분지 Lv134~138 몬스터 드롭',
  junk_t33: '검은가시 분지 Lv139~143 몬스터 드롭',
  junk_t34: '검은가시 분지 Lv144~148 몬스터 드롭',
  junk_t35: '검은가시 분지 Lv149~153 몬스터 드롭',
  junk_t36: '검은가시 분지 Lv154~158 몬스터 드롭',
  junk_t37: '검은가시 분지 Lv159 이상 몬스터 드롭',
  junk_t38: '지옥 분지 Lv500~539 몬스터 드롭',
  junk_t39: '지옥 분지 Lv540~599 몬스터 드롭',
  junk_t40: '지옥 분지 Lv600 이상 네임드·보스 드롭',
};
/**
 * ★★ **`JUNK_KO` 에는 잡템만 있는 게 아니다** — 서적·각인·날개·무게 각인·생명의 돌·
 * 거울·종의 **이름표**도 거기 산다. 이 루프가 표 전체에 잡템 설명을 도장 찍는 바람에
 * **무결의 날개가 "쓸 데는 없다. 잡화상에게 0은화에 판다"** 를 달고 다녔고, 같은
 * 툴팁의 가격 줄("팔 수 없다")과 자기모순이었다(사용자 스크린샷 실물, 2026-09-04).
 * 특수 키는 각자의 정본에서 **실제 용도**를 도출한다 — 표가 늘면 설명이 따라 생긴다.
 */
const specialTip = (k) => {
  const book = PASSIVE_BY_KEY.get(k);
  if (book) {
    return { name: book.name, desc: `가방에서 읽으면 <b>${esc(book.effects[0])}</b><br><b>습득한 단계는 사망해도 유지된다.</b> 같은 책을 읽을 때마다 한 단계씩 끝없이 강해진다. 새 세계에서는 초기화된다.`, from: passiveBookSourceText(book) };
  }
  const imprint = SKILL_IMPRINT_BY_KEY.get(k);
  if (imprint) {
    return { name: imprint.name, desc: `가방에서 각인석을 사용할 때마다 한 단계씩 끝없이 강해지고, 스킬 탭에서 <b>전투 각인</b>으로 장착한다. 습득한 단계는 사망해도 유지되며, 새 세계에서는 초기화된다.`, from: imprint.from };
  }
  const carry = CARRY_BY_KEY.get(k);
  if (carry) {
    return { name: carry.name, desc: `가방에서 단계 순서대로 사용하면 <b>무게 한도 +${carry.cap.toLocaleString()}</b>. 효과는 누적되며 사망·재접속 후에도 유지된다. 새 세계에서는 초기화된다.`, from: carry.from };
  }
  const wing = WING_BY_KEY.get(k);
  if (wing) {
    return { name: wing.name, desc: `${wing.desc}<br>가방에서 펼치면 <b>별도 날개 슬롯</b>에 장착된다.`, from: '유리심연의 상징 보스.' };
  }
  const pardon = PARDON_BY_KEY.get(k);
  if (pardon) {
    return { name: pardon.name, desc: `가방에 소지한 동안 <b>누계 현상금이 ${pardon.face.toLocaleString()} 은화보다 작으면</b> 수배가 발동하지 않는다. 액면 이상이면 보호가 사라진다. 여러 장은 합산하지 않고 최고 액면 하나만 적용한다. <b>범죄레벨·처형은 면제하지 않는다.</b> 죽으면 바닥에 떨어뜨린다.`, from: '길드 가입 후 현상금 게시판에서 명예포인트로 구매. 경비병에게서도 매우 드물게 드롭된다.' };
  }
  if (k === LIFE_STONE_KEY) return { name: '생명의 돌', desc: `가방에서 사용하면 <b>내 레벨 +${LIFE_STONE_LEVELS}</b>, 마지막 날 전에 신전에 봉헌하면 <b>세계 수명 +${LIFE_STONE_SECS / 3600}시간</b>. 두 선택은 각각 돌 1개를 쓴다.<br><b>계정 영혼석 제작</b>에도 쓴다. 필요한 돌과 은화는 가방·신전에서 확인할 수 있다.`, from: '모든 자연 보스가 아주 드물게 떨군다.' };
  // UNI-Q192 — 고용에 드는 수·데리고 다닐 수 있는 수는 운영 설정이라 여기 적지 않는다(가방 상세가 서버 값을 보인다)
  if (k === GUARD_SHARD_KEY) return { name: GUARD_SHARD_NAME, desc: '정해진 수를 모으면 가방에서 <b>보디가드</b>를 고용한다. 보디가드는 나를 따라다니며 <b>나를 먼저 친 사람과 나를 노리는 몬스터</b>에게 반격하고, 너무 멀어지면 돌아온다. 거래·창고 보관이 되고 죽으면 바닥에 떨어뜨린다. 잡화상은 사지 않는다.', from: '경비병을 쓰러뜨리면 가끔 나온다 — 쓰러뜨릴 때마다 범죄레벨 💀 가 오른다.' };
  return null;
};
/**
 * ⚠ 손 정본(강화석·축복석)이 먼저다 — 이 루프가 그 둘을 잡템 템플릿으로 **덮어쓰고 있었다.**
 * ★ 돌리는 우주는 `JUNK_KO` 가 아니라 **정의 표 전체**다 — 면죄부(pardon_t*)는 JUNK_KO 에
 *   없어서 툴팁 제목이 원시 키로 뜨고 설명이 비어 있었다(JUNK_KO 만 돌던 병의 재발).
 *   표(정의 배열)가 늘면 여기와 검사기(check-item-names) 대상이 같이 는다.
 */
const TIP_KEYS = new Set([
  ...Object.keys(JUNK_KO),
  ...PASSIVE_BY_KEY.keys(), ...SKILL_IMPRINT_BY_KEY.keys(), ...CARRY_BY_KEY.keys(),
  ...WING_BY_KEY.keys(), ...PARDON_BY_KEY.keys(),
  LIFE_STONE_KEY, GUARD_SHARD_KEY,
]);
for (const k of TIP_KEYS) {
  TIPS[k] = TIPS[k] ?? specialTip(k) ?? {
    name: junkName(k),
    // 개당 값은 세계마다 다를 수 있어(6세계부터 2배) 아래 보유 줄이 지금 값으로 말한다
    desc: '판매용 잡템. <b>잡화상</b>에게 팔 수 있다(판매 합계에서 왕도 세금을 뗀다).',
    from: JUNK_FROM[k] ?? '',
  };
}

let el = null;
function ensure() {
  if (el) return el;
  el = document.createElement('div');
  el.id = 'tip';
  document.getElementById('game').appendChild(el);
  return el;
}

export function hideTip() { if (el) el.classList.remove('on'); }

/** 마우스 위치 근처에 띄운다. 화면 밖으로 나가지 않게 접는다 */
function place(x, y) {
  const e = ensure();
  const w = e.offsetWidth || 240, h = e.offsetHeight || 90;
  const px = Math.min(Math.max(8, x + 14), innerWidth - w - 8);
  const py = y + h + 20 > innerHeight ? y - h - 12 : y + 18;
  e.style.left = px + 'px';
  e.style.top = Math.max(8, py) + 'px';
}

function shell(title, color, body, foot) {
  return `<div class="tt" style="--c:${color}">${title}</div>`
    + `<div class="tb">${body}</div>`
    + (foot ? `<div class="tf">${foot}</div>` : '');
}

/** 가방 칸에서 부른다 — key 는 장비 id, 'j:재료', 'c:소모품' */
export function showTip(key, x, y) {
  const y0 = S.you; if (!y0) return;
  const e = ensure();
  let html = '';

  if (key.startsWith('j:')) {
    const id = key.slice(2);
    const t = TIPS[id];
    const n = (y0.inv.junk || {})[id] || 0;
    // 11-01 — 그 세계의 판매가 배율(6세계부터 2)을 곱한 값
    const unit = JUNK_PRICE[id] ? Math.round(JUNK_PRICE[id] * junkPriceMultOf(y0)) : 0;
    const price = unit ? `개당 ${unit.toLocaleString()} 은화 · 합계 ${(unit * n).toLocaleString()} (왕도 세금 공제 전)` : '팔 수 없다';
    // ⚠ 이름 폴백은 단일 문(junkName) — `JUNK_KO[id] || id` 사본이 낡아 원시 키가 떴다
    html = shell(t ? t.name : junkName(id), id === BSTONE ? '#f0d26b' : '#d4a83c',
      (t ? t.desc : '') + `<br><span class="qt">보유 ${n.toLocaleString()}개 · ${price}</span>`,
      t ? t.from : '');
  } else if (key.startsWith('c:')) {
    const id = key.slice(2);
    const s = POTIONS.find((q) => q.id === id);
    html = shell(s ? s.name : id, '#c9736c',
      (s ? s.desc : '') + `<br><span class="qt">보유 ${((y0.inv.consum || {})[id] || 0).toLocaleString()}개</span>`,
      id === 'scroll' ? '피격되면 시전이 끊긴다.' : '');
  } else if (key.startsWith('a:')) {
    const a = ACC_BY_ID.get(key.slice(2));
    if (a) html = shell(a.name, '#8cbd87', a.desc, '한 번에 하나만 착용한다.');
  } else {
    const eq = (y0.inv.equips || []).find((q) => q.id === key);
    if (!eq) { hideTip(); return; }
    const g = gradeOf(eq.grade || 0);
    const worn = Object.values(y0.inv.equipped || {}).includes(eq.id);
    html = shell(
      // ⚠ 부위 이름은 표에서 — 삼항으로 나누면 투구·장갑·신발·망토가 전부 「갑옷」이 된다
      eq.guildBound != null ? esc(eqTitle(eq)) : `${eq.crude ? '조잡한' : g.name} ${SLOT_NAME[eq.slot] ?? eq.slot} +${eq.plus}`,
      eq.crude ? '#8a7f6b' : GRADE_COL[g.id],
      `${rollDesc(eq)}<br>`
      + (eq.blessed ? '<span class="bless">★축복</span> — 한 단계만큼 더 세다<br>' : '')
      + guildRingInfo(eq, y0)
      + templeRingInfo(eq, y0)
      + `안전수치 <b>+${eq.crude ? 5 : g.safe}</b> · ${g.id >= MAX_GRADE && !eq.crude
        ? '<b>+10 이후 강화 상한 없음</b>' : '상한 <b>+10</b>'}<br>`
      + `<span class="qt">${eq.crude
        ? '조잡한 장비는 안전수치까지만 강화할 수 있다. 머지·강화석 강화는 불가능하다.'
        : ascendRule(eq.slot, g.id)}</span>`,
      eq.guildBound != null ? '계정 귀속 · 판매·거래·드롭·분해 불가. 귀속 반지는 같은 계정의 같은 반지에만 합성 재료로 사용할 수 있다.'
        : isTempleRingItem(eq) ? `${worn ? '착용 중 · ' : ''}판매·분해 불가`
        : worn ? '착용 중 · 벗은 후 판매·분해 가능' : '가방에서 선택해 착용·강화할 수 있다.');
  }

  e.innerHTML = html;
  pixelGlyphText(e); // R169 — 표의 이모지는 도트 글리프로
  e.classList.add('on');
  place(x, y);
}
